/**
 * Charted places retain the voyage layer's positions and berth geometry. Artwork is assigned
 * once over the complete chart, without replacement and within its declared region.
 * Scenery is a finite catalogue of deliberate physical islands, never a random field.
 * A missing asset leaves a diagnostic gap; neither a camera move nor a save seed creates art.
 */

import { areaAt, AREA_BY_PLACE, BERTH_CLEARANCE_LEAGUES, SPIRAL_LEAGUES_PER_RADIAN, type PlaceExtent, type SeaAreaId } from '../voyage/index';
import { SCENERY_CATALOGUE, type SceneryPlacement } from './scenery-catalogue';
import { PX_PER_LEAGUE_ACROSS, PX_PER_LEAGUE_UP } from './scale';

/** Compatibility clearance for the retained skerries. Production measures each actual body. */
export const FIELD_SPRITE_MAX_HALF_LEAGUES = 172 / 2 / PX_PER_LEAGUE_ACROSS;
export const TRACK_CLEARANCE_LEAGUES = FIELD_SPRITE_MAX_HALF_LEAGUES + BERTH_CLEARANCE_LEAGUES;

/**
 * THE WATER A CHARTED PLACE OWNS, as it was modelled when every place islet was a 203 px settled
 * sprite: 8 leagues from the place point, no field islet inside it.
 *
 * That 8 was measured from the place POINT, so it silently contained the sprite: a 203 px islet
 * is 2.5375 leagues to its own edge, and the remaining 5.4625 leagues were the clear water. An
 * authored island is 2,483 px = 31.04 leagues to its edge, and the same constant would have put
 * generated islets on top of a hand-drawn village. So the clearance is now measured from the
 * ISLET'S EDGE and the two halves are named. For a 203 px sprite it sums back to exactly 8.
 */
export const PLACE_CLEARANCE_LEAGUES = 8;
/**
 * The settled sprites the 8 was modelled on are 203 px across: 101.5 px / 40 px per league. Still
 * the widest thing in the harbour pool after it grew - cliff is 187 px and spire 140 - so the
 * fallback half-width for a caller that names no sprite is unchanged, and `islets.test.ts` holds
 * it against the pool rather than against the two sprites it was measured on.
 */
export const GENERIC_PLACE_HALF_WIDTH_LEAGUES = 2.5375;
/** So this much of the 8 was ever clear water. DERIVED from the two constants above. */
export const PLACE_EDGE_CLEARANCE_LEAGUES = PLACE_CLEARANCE_LEAGUES - GENERIC_PLACE_HALF_WIDTH_LEAGUES;

/**
 * How far a generated islet's FOOT sits north of the place point, in art px. MODELLED, and
 * recovered rather than invented: round 4 shipped a flat 4.6-league offset for a sprite whose
 * anchor is 97 px above its own foot, so the clear water between the foot and the point was
 * 4.6 x 34.615 - 97 = 62.15 px. Rounding it to 62 moves the generated fallback by 0.15 px up-screen and
 * makes the rule work for a sprite of any height, which the flat constant did not.
 *
 * An AUTHORED island does not use this. Its anchor is a place a person chose - for Murrin Roost,
 * the water off the mouth of its bay - so the offset is zero and the drawing decides.
 */
export const PLACE_FOOT_CLEARANCE_PX = 62;

/** A baked sprite as this file needs it: its box, and the point in it that sits on the world position. */
export interface IsletSprite {
  readonly id: string;
  readonly w: number;
  readonly h: number;
  readonly anchor: { readonly x: number; readonly y: number };
  /** True when a person drew this island. Its anchor is authored and nothing about it is derived. */
  readonly authored: boolean;
}

export interface Islet {
  readonly id: string;
  /** The baked sprite id (e.g. 'blackstage-7300', 'murrin-roost'). */
  readonly sprite: string;
  readonly x: number;
  readonly y: number;
  readonly source: 'place' | 'field';
}

export interface PlaceLike {
  readonly id: string;
  readonly position: { readonly x: number; readonly y: number };
  /**
   * WHERE THE VOYAGE LAYER SAYS THE ISLAND IS. `ChartedPlace` carries it through from the seed
   * table, so the renderer reads the placement rather than deriving a second one. A place with no
   * extent is drawn the way round 4 drew it, which is what keeps a bare `{id, position}` caller
   * working; nothing in the runtime is one.
   */
  readonly extent?: PlaceExtent;
}

/** Where the sprites for charted places come from. */
export interface PlaceIsletSources {
  /** Every sprite that can stand for a place, by id. */
  readonly sprites: ReadonlyMap<string, IsletSprite>;
  /**
   * THE HARBOUR POOL: every generated sprite whose silhouette can carry a named place, in the
   * order `harbourSpriteIds` put them. This used to be `settled`, and the name was the defect -
   * see the header of this file.
   */
  readonly harbour: readonly string[];
  /** place id -> the sprite id of the island a person drew for it. */
  readonly authored: ReadonlyMap<string, string>;
  /**
   * THE ISLAND THAT WAS DRAWN UNDER THIS PLACE'S OWN NAME. place id -> sprite id.
   *
   * NOT `authored`, AND THE DISTINCTION IS LOAD-BEARING. `authored` means a person drew this
   * island and ITS ANCHOR IS AUTHORED: `placeIsletOffsetLeagues` returns 0 for such a sprite,
   * `authoredReachLeagues` widens the cull margin off it, and `src/app/world-entry.ts` gates
   * behaviour on membership of that map. Every island here is GENERATED and its anchor is
   * DERIVED like any other, so putting these fifteen in `authored` would have dropped all of
   * them onto their place point. `sprite.authored` stays false for everything in this map.
   *
   * Baked by `tools/world-render/bind-places.mjs` into `src/render/baked/place-islands.json`;
   * read through `placeIslandBinding`. The match is made once, at bake time, because both sides
   * are static: the place table is a module constant and the declarations are a JSON file.
   */
  readonly named?: ReadonlyMap<string, string>;
  /** sprite id -> the area it was drawn for. Baked; `undefined` for a hand-authored island. */
  readonly spriteArea?: ReadonlyMap<string, SeaAreaId>;
  /** place id -> the area it is written into. Baked from `AREA_BY_PLACE`. */
  readonly placeArea?: ReadonlyMap<string, SeaAreaId>;
  /**
   * WHICH SPRITE EACH PLACE WEARS, solved once by `assignPlaceSprites` over the WHOLE chart and
   * carried here so that a caller holding a culled list still gets the same answer. A place with
   * no entry is unresolved. Culling must never give it a new fallback picture.
   */
  readonly assignment?: ReadonlyMap<string, string>;
}

/** The shape `tools/world-render/bind-places.mjs` writes. Read by `placeIslandBinding`. */
export interface BakedPlaceIslands {
  readonly named: Readonly<Record<string, string>>;
  readonly spriteArea: Readonly<Record<string, string>>;
  readonly supply: readonly { readonly area: string; readonly pool: number; readonly places: number; readonly short: number }[];
}

/**
 * THE BAKED BINDING, AS THE THREE MAPS `assignPlaceSprites` READS.
 *
 * `placeArea` is derived here from `AREA_BY_PLACE` rather than baked a second time: it is already
 * a module constant on the voyage side, and one number in two files is how a number stops being
 * one (CONTRACT, the standing note on quoted figures).
 */
export function placeIslandBinding(baked: BakedPlaceIslands): {
  named: ReadonlyMap<string, string>;
  spriteArea: ReadonlyMap<string, SeaAreaId>;
  placeArea: ReadonlyMap<string, SeaAreaId>;
} {
  return {
    named: new Map(Object.entries(baked.named)),
    spriteArea: new Map(Object.entries(baked.spriteArea) as [string, SeaAreaId][]),
    placeArea: AREA_BY_PLACE,
  };
}

/**
 * WHAT THE DEAL READS. Everything but `sprites`, `harbour` and `authored` is optional, so a
 * caller without a binding can still request an unregionalised finite deal.
 */
export type PlaceDealSources = Pick<PlaceIsletSources, 'sprites' | 'harbour' | 'authored' | 'named' | 'spriteArea' | 'placeArea'>;

/** A manifest islet as the pool builder needs it. */
export interface HarbourCandidate {
  readonly id: string;
  readonly kind: string;
  readonly harbour?: boolean;
  readonly area?: string;
}

/**
 * EVERY SPRITE A NAMED PLACE MAY WEAR, IN THE ORDER THEY ARE HANDED OUT.
 *
 * -- WHAT THIS REPLACES, MEASURED --------------------------------------------
 *
 * The pool was `manifest.islets.filter((s) => s.kind === 'settled')` and the shipped manifest
 * carries exactly TWO sprites of that kind for twenty named places. MEASURED on the shipped
 * tree at f4530e0, walking `CHARTED_PLACES` through the old `spriteForPlace`: one old fallback was
 * worn by 14 of the 20, another by 5, and Murrin Roost - the one drawn island - by 1. Six
 * consecutive places, `place-02` through `place-07`, wore the identical picture. So it was not
 * even two pictures evenly split; the old key hashed `id.length` (constant at 8 for every id)
 * with the LAST CHARACTER of the id, which over ten digits is not a balanced bit.
 *
 * -- WHY A DECLARED PROPERTY AND NOT A KIND FILTER ---------------------------
 *
 * `settled` is the narrowest possible reading of what a place can look like. A fishing station
 * under a cliff is a place; a beacon on a spire is a place; and both cliff sprites and both
 * spire sprites can hold one. That is a judgement about the SPRITE, so it is declared beside the
 * sprite in `tools/world-render/stature-islets.json` and carried through the bake into
 * `manifest.json`. This function reads the declaration and never the id.
 *
 * -- THE ORDER, WHICH IS THE PART THAT DOES WORK -----------------------------
 *
 * Sorted by id, then dealt ROUND-ROBIN ACROSS KINDS. Kind was the wrong thing to filter on and
 * is the right thing to spread on: it is the coarsest similarity signal the manifest has, and
 * dealing across it is what stops two lookalike sprites landing on places the player sails
 * between. The current pool is ordered by id and interleaved by each declared kind, so
 * consecutive
 * charted places are never the same KIND, let alone the same sprite.
 *
 * Sorting first is what makes it stable: the answer depends on the SET of eligible sprite ids
 * and not on the order `bake.mjs` happened to write them in.
 */
export function harbourSpriteIds(islets: readonly HarbourCandidate[]): string[] {
  const eligible = islets.filter((s) => s.harbour === true).map((s) => ({ id: s.id, kind: s.kind }));
  eligible.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const buckets = new Map<string, string[]>();
  for (const s of eligible) {
    const b = buckets.get(s.kind);
    if (b === undefined) buckets.set(s.kind, [s.id]);
    else b.push(s.id);
  }
  const lists = [...buckets.values()];
  const out: string[] = [];
  for (let round = 0; out.length < eligible.length; round += 1) {
    for (const list of lists) {
      const id = list[round];
      if (id !== undefined) out.push(id);
    }
  }
  return out;
}

/** Explicit retained scenery only; new, rejected and harbour assets never enter by omission. */
export function fieldSpriteIds(islets: readonly HarbourCandidate[]): string[] {
  return SCENERY_CATALOGUE.filter((placement) => islets.some((sprite) =>
    sprite.id === placement.sprite && sprite.harbour === false &&
    sprite.area?.toLowerCase().replace(/[^a-z]/g, '') === placement.area,
  )).map((placement) => placement.sprite);
}

/** Assign the complete chart once: drawn/named identities first, then unused regional art.
 * Never recycle or cross regions. Unresolved places remain in the voyage chart and are
 * reported by placeRegionShortfall/placeVarietyShortfall; the simulation is not changed.
 */
export function assignPlaceSprites(
  places: readonly PlaceLike[],
  src: PlaceDealSources,
): Map<string, string> {
  const out = new Map<string, string>();
  const taken = new Set<string>();
  /**
   * The islands that were made for a place. A second pass may never hand one of these to somebody
   * else: an island drawn under Tangsay's name showing up on a place three coils away is exactly
   * the repetition CONTRACT 1.5m(a) is about, and it is worse than an ordinary repeat because the
   * picture is the place's identity.
   */
  const reserved = new Set<string>();

  // Pass one: the pictures that were made for a place. Order inside the pass does not matter,
  // because no two places can claim the same island - `bind-places.mjs` declines an ambiguous
  // name - and it must run before any dealing, or a rotating cursor could spend an island on
  // the place next door before its own place is reached.
  for (const p of places) {
    const drawn = src.authored.get(p.id);
    if (drawn !== undefined && src.sprites.has(drawn) && !taken.has(drawn)) {
      out.set(p.id, drawn);
      taken.add(drawn);
      reserved.add(drawn);
      continue;
    }
    const named = src.named?.get(p.id);
    const area = src.placeArea?.get(p.id);
    if (named !== undefined && src.sprites.has(named) && src.harbour.includes(named) && !taken.has(named) &&
      (area === undefined || src.spriteArea?.get(named) === area)) {
      out.set(p.id, named);
      taken.add(named);
      reserved.add(named);
    }
  }

  // Pass two: everything else, in chart order, out of its own region's share of the pool.
  for (const p of places) {
    if (out.has(p.id)) continue;
    if (src.harbour.length === 0) continue;

    const area = src.placeArea?.get(p.id);
    const picked = src.harbour.find((id) => src.sprites.has(id) && !taken.has(id) && !reserved.has(id) &&
      (area === undefined || src.spriteArea?.get(id) === area));
    // Missing regional art stays unresolved. Never borrow another climate or wrap the pool.
    if (picked === undefined) continue;
    out.set(p.id, picked);
    taken.add(picked);
  }
  return out;
}

/** Report unresolved regional art without substituting another region. */
export function placeRegionShortfall(
  places: readonly PlaceLike[],
  src: PlaceDealSources,
): { readonly area: SeaAreaId; readonly pool: number; readonly places: number; readonly short: number; readonly without: string[] }[] {
  const assignment = assignPlaceSprites(places, src);
  const poolByArea = new Map<SeaAreaId, number>();
  for (const id of src.harbour) {
    const a = src.spriteArea?.get(id);
    if (a === undefined) continue;
    poolByArea.set(a, (poolByArea.get(a) ?? 0) + 1);
  }
  const byArea = new Map<SeaAreaId, { places: number; without: string[] }>();
  for (const p of places) {
    if (src.authored.has(p.id)) continue; // a drawn island is its own supply
    const a = src.placeArea?.get(p.id);
    if (a === undefined) continue;
    const row = byArea.get(a) ?? { places: 0, without: [] };
    row.places += 1;
    const worn = assignment.get(p.id);
    if (worn === undefined || src.spriteArea?.get(worn) !== a) row.without.push(p.id);
    byArea.set(a, row);
  }
  return [...byArea.entries()]
    .map(([area, row]) => ({ area, pool: poolByArea.get(area) ?? 0, places: row.places, short: row.without.length, without: row.without }))
    .filter((r) => r.short > 0)
    .sort((a, b) => (a.area < b.area ? -1 : 1));
}

/** Actual assignment gaps, including regional shortages despite spare art elsewhere. */
export function placeVarietyShortfall(
  places: readonly PlaceLike[],
  src: PlaceDealSources,
): { readonly undrawn: number; readonly pool: number; readonly missing: number; readonly worstRepeat: number } {
  const undrawn = places.filter((p) => {
    const drawn = src.authored.get(p.id);
    return drawn === undefined || !src.sprites.has(drawn);
  }).length;
  const pool = src.harbour.length;
  const assignment = assignPlaceSprites(places, src);
  const counts = new Map<string, number>();
  for (const sprite of assignment.values()) counts.set(sprite, (counts.get(sprite) ?? 0) + 1);
  return {
    undrawn,
    pool,
    missing: places.filter((place) => !assignment.has(place.id)).length,
    worstRepeat: Math.max(0, ...counts.values()),
  };
}

/** A disc of water no field islet may sit in. Leagues. */
export interface ClearDisc {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

const TWO_PI = 2 * Math.PI;

/**
 * Radial distance from a point to the nearest coil of the track, r = A * theta with x = A theta
 * sin theta, y = A theta cos theta (the voyage layer's spiral; see track.ts). Coils are 2 pi A
 * apart, so the nearest one is found by rounding, and the radial gap is the distance to within a
 * fraction of a league everywhere but the innermost turn, where it is conservative.
 */
export function distanceToTrack(x: number, y: number): number {
  const A = SPIRAL_LEAGUES_PER_RADIAN;
  const r = Math.hypot(x, y);
  if (r < 1e-9) return 0;
  let phi = Math.atan2(x, y);
  if (phi < 0) phi += TWO_PI;
  const k0 = Math.round((r / A - phi) / TWO_PI);
  let best = Infinity;
  for (let k = k0 - 1; k <= k0 + 1; k++) {
    const theta = phi + TWO_PI * k;
    if (theta < 0) continue;
    const d = Math.abs(r - A * theta);
    if (d < best) best = d;
  }
  return best;
}

/** Read the whole-chart assignment. Bare lookups resolve explicit identity only. */
export function spriteForPlace(place: PlaceLike, src: PlaceIsletSources): string | undefined {
  // An explicit unresolved assignment is authoritative, including for culled lookups.
  if (src.assignment !== undefined) return src.assignment.get(place.id);
  const drawn = src.authored.get(place.id);
  if (drawn !== undefined && src.sprites.has(drawn)) return drawn;
  // Without a deal, only an explicit identity can be answered safely.
  const named = src.named?.get(place.id);
  if (named !== undefined && src.sprites.has(named) && src.harbour.includes(named) &&
    (src.placeArea?.get(place.id) === undefined || src.spriteArea?.get(named) === src.placeArea.get(place.id))) return named;
  return undefined;
}

/**
 * How far north of the place point the islet's anchor sits, when no extent says otherwise.
 * Leagues, zero for a drawn island. THE LEGACY RULE - see `placeIsletAnchorLeagues`.
 */
export function placeIsletOffsetLeagues(sprite: IsletSprite): number {
  if (sprite.authored) return 0;
  return (sprite.h - sprite.anchor.y + PLACE_FOOT_CLEARANCE_PX) / PX_PER_LEAGUE_UP;
}

/**
 * WHERE THE ISLET'S ANCHOR GOES, in world leagues. This is the round's placement change.
 *
 * CONTRACT 1.5n(b): "Placement stops being a decoration applied after the track exists and becomes
 * part of laying the track. Note this cuts against `placeIslets` as written, which applies no track
 * clearance at all and whose fixed-north offset moves 11 of 20 islands toward the track."
 *
 * MEASURED, before: the flat 4.593-league north offset moved ELEVEN of the twenty charted islands
 * toward the track and nine away, and left place-01's islet 0.419 leagues from a track its own body
 * reaches 2.54 leagues across. North is not a direction that knows where the track is.
 *
 * So the offset is no longer computed here. The voyage layer publishes `extent.centreOffsetLeagues`
 * for every charted place - solved by `src/voyage/berth.ts` against the spiral's own coils - and
 * this function puts the sprite's CENTRE there, converting to the anchor the sprite actually draws
 * from. ONE rule, and the picture cannot drift from the simulation because they read the same field.
 *
 * For MURRIN ROOST the two agree exactly: its extent was measured off its own sprite and anchor, so
 * `(anchor - box centre)` cancels the published centre offset and the island lands on the same
 * world pixel it did before this change. `islets.test.ts` asserts that rather than claiming it.
 */
export function placeIsletAnchorLeagues(place: PlaceLike, sprite: IsletSprite): { x: number; y: number } {
  const e = place.extent;
  if (e === undefined) {
    return { x: place.position.x, y: place.position.y + placeIsletOffsetLeagues(sprite) };
  }
  const centreX = place.position.x + e.centreOffsetLeagues.x;
  const centreY = place.position.y + e.centreOffsetLeagues.y;
  return {
    x: centreX + (sprite.anchor.x - sprite.w / 2) / PX_PER_LEAGUE_ACROSS,
    y: centreY - (sprite.anchor.y - sprite.h / 2) / PX_PER_LEAGUE_UP,
  };
}

/** The half-width of a sprite in leagues: what the place's clearance is measured from. */
export function halfWidthLeagues(sprite: IsletSprite): number {
  return sprite.w / 2 / PX_PER_LEAGUE_ACROSS;
}

export function placeIslets(places: readonly PlaceLike[], src: PlaceIsletSources): Islet[] {
  const out: Islet[] = [];
  const assigned = src.assignment === undefined ? { ...src, assignment: assignPlaceSprites(places, src) } : src;
  for (const p of places) {
    const spriteId = spriteForPlace(p, assigned);
    if (spriteId === undefined) continue;
    const sprite = src.sprites.get(spriteId);
    if (sprite === undefined) continue;
    const at = placeIsletAnchorLeagues(p, sprite);
    out.push({
      id: `place:${p.id}`,
      sprite: spriteId,
      x: at.x,
      y: at.y,
      source: 'place',
    });
  }
  return out;
}

/**
 * The water each charted place owns, centred on the PLACE POINT rather than on its islet, which
 * is how round 4 measured it and is what keeps the generated sea identical where nothing was
 * drawn. A disc rather than the sprite's box: an authored island then clears open water north of
 * itself as well, which is MODELLED and is wanted - a 318 m island should not have a generated
 * lump sitting off its cliff.
 */
export function placeClearanceDiscs(places: readonly PlaceLike[], src: PlaceIsletSources): ClearDisc[] {
  const out: ClearDisc[] = [];
  for (const p of places) {
    const spriteId = spriteForPlace(p, src);
    const sprite = spriteId === undefined ? undefined : src.sprites.get(spriteId);
    const half = sprite === undefined ? GENERIC_PLACE_HALF_WIDTH_LEAGUES : halfWidthLeagues(sprite);
    // THE DISC STAYS ON THE PLACE POINT AND GROWS BY THE BERTH. The island is no longer centred on
    // the point, so a disc that ignored the offset would leave clear water where the island now is
    // and claim water where it no longer is. Growing rather than moving is deliberate: the disc can
    // only ever swallow MORE field islets than round 4's did, never uncover one, so the generated
    // sea changes in one direction and every change removes an overlap.
    const e = p.extent;
    const reach = e === undefined ? 0 : Math.hypot(e.centreOffsetLeagues.x, e.centreOffsetLeagues.y);
    out.push({ x: p.position.x, y: p.position.y, r: reach + half + PLACE_EDGE_CLEARANCE_LEAGUES });
  }
  return out;
}

export interface SceneryIssue { readonly id: string; readonly sprite: string; readonly reason: string; }

/** Resolve once against the complete chart. Invalid or unavailable art leaves an explicit gap. */
export function resolveSceneryCatalogue(
  places: readonly PlaceLike[],
  src: PlaceIsletSources,
  eligible: readonly string[],
  catalogue: readonly SceneryPlacement[] = SCENERY_CATALOGUE,
): { readonly islets: Islet[]; readonly issues: SceneryIssue[] } {
  const islets: Islet[] = [];
  const issues: SceneryIssue[] = [];
  const ids = new Set<string>();
  const sprites = new Set<string>();
  const chartSprites = new Set([
    ...(src.assignment ?? assignPlaceSprites(places, src)).values(),
    ...src.authored.values(), ...(src.named?.values() ?? []),
  ]);
  const discs = placeClearanceDiscs(places, src);
  // Also protect the actual rendered bodies. A future tall drawing can outgrow the legacy
  // place-water disc, which was sized from width; missing art still reserves its voyage extent.
  const chartBodies = places.map(place => {
    const spriteId = spriteForPlace(place, src);
    const sprite = spriteId === undefined ? undefined : src.sprites.get(spriteId);
    if (sprite) {
      const anchor = placeIsletAnchorLeagues(place, sprite);
      return { x: anchor.x + (sprite.w / 2 - sprite.anchor.x) / PX_PER_LEAGUE_ACROSS,
        y: anchor.y - (sprite.h / 2 - sprite.anchor.y) / PX_PER_LEAGUE_UP,
        r: Math.hypot(sprite.w / 2 / PX_PER_LEAGUE_ACROSS, sprite.h / 2 / PX_PER_LEAGUE_UP) };
    }
    const extent = place.extent;
    return { x: place.position.x + (extent?.centreOffsetLeagues.x ?? 0),
      y: place.position.y + (extent?.centreOffsetLeagues.y ?? 0),
      r: extent ? Math.hypot(extent.halfWidthLeagues, extent.halfHeightLeagues) : GENERIC_PLACE_HALF_WIDTH_LEAGUES };
  });
  const radii: number[] = [];
  for (const entry of catalogue) {
    const sprite = src.sprites.get(entry.sprite);
    let reason: string | undefined;
    if (ids.has(entry.id) || sprites.has(entry.sprite)) reason = 'duplicate physical identity or sprite';
    ids.add(entry.id); sprites.add(entry.sprite);
    if (!reason && (!eligible.includes(entry.sprite) || !sprite)) reason = 'no eligible baked scenery asset';
    if (!reason && chartSprites.has(entry.sprite)) reason = 'sprite reserved for a charted place';
    if (!reason && (![entry.x, entry.y].every(Number.isFinite) || areaAt(entry) !== entry.area || src.spriteArea?.get(entry.sprite) !== entry.area)) reason = 'placement or sprite belongs to a different region';
    // Radius about the actual draw anchor encloses every corner, including asymmetric anchors.
    const radius = sprite ? Math.hypot(Math.max(sprite.anchor.x, sprite.w - sprite.anchor.x) / PX_PER_LEAGUE_ACROSS,
      Math.max(sprite.anchor.y, sprite.h - sprite.anchor.y) / PX_PER_LEAGUE_UP) : 0;
    if (!reason && distanceToTrack(entry.x, entry.y) < radius + BERTH_CLEARANCE_LEAGUES) reason = 'sprite body reaches the track clearance';
    if (!reason && discs.some(d => Math.hypot(entry.x - d.x, entry.y - d.y) < d.r + radius)) reason = 'sprite body reaches charted-place clearance';
    if (!reason && chartBodies.some(d => Math.hypot(entry.x - d.x, entry.y - d.y) < d.r + radius + BERTH_CLEARANCE_LEAGUES)) reason = 'sprite body reaches a charted island body';
    if (!reason && islets.some((other, i) => Math.hypot(entry.x - other.x, entry.y - other.y) < radius + radii[i]! + BERTH_CLEARANCE_LEAGUES)) reason = 'sprite body overlaps another scenery island';
    if (reason) { issues.push({ id: entry.id, sprite: entry.sprite, reason }); continue; }
    islets.push({ id: entry.id, sprite: entry.sprite, x: entry.x, y: entry.y, source: 'field' });
    radii.push(radius);
  }
  return { islets, issues };
}

/**
 * Cull a finite world catalogue. Seed is retained only for older instrument callers.
 * A legacy string pool can select explicit catalogue entries, never create a physical island.
 * Production passes the complete resolved catalogue, with complete-chart clearance already checked.
 */
export function fieldIslets(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  _seed: number,
  sprites: readonly string[] | readonly Islet[],
  keepClear: readonly ClearDisc[],
): Islet[] {
  const catalogue: readonly Islet[] = typeof sprites[0] === 'string'
    ? SCENERY_CATALOGUE.filter(entry => (sprites as readonly string[]).includes(entry.sprite))
      .map(entry => ({ id: entry.id, sprite: entry.sprite, x: entry.x, y: entry.y, source: 'field' }))
    : sprites as readonly Islet[];
  return catalogue.filter(entry => entry.x >= x0 && entry.x <= x1 && entry.y >= y0 && entry.y <= y1 &&
    keepClear.every(disc => Math.hypot(entry.x - disc.x, entry.y - disc.y) >= disc.r));
}
